// Dashboard View - Main hub after login
import React from 'react';
import { BookOpen, MessageSquare, Map, ChevronRight, Star, TrendingUp, Users, Calendar, Clock, Search, Package, Compass, Image } from 'lucide-react';
import { CigarIcon } from '../components/Icons';
import { TastingNote } from '../types';
import { FESTIVALS, SAMPLE_VENDORS } from '../data/festivals';
import { TodaysSmokeCard, AshWisdomCard, PairingOfTheWeekCard } from '../components/DailyRitual';
import { Cigar, getTodaysSmoke } from '../data/cigars';

// Helper to calculate days until festival
const getDaysUntil = (dateStr: string | undefined): number | null => {
  if (!dateStr) return null;

  // Parse formats like "December 6, 2026" or "April 17-20, 2026"
  const dateMatch = dateStr.match(/(\w+)\s+(\d+)(?:-\d+)?,\s+(\d{4})/);
  if (!dateMatch) return null;

  const [, month, day, year] = dateMatch;
  const festivalDate = new Date(`${month} ${day}, ${year}`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  festivalDate.setHours(0, 0, 0, 0);

  const diffTime = festivalDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : null;
};

interface DashboardViewProps {
  user: { name: string; id: string };
  notes: TastingNote[];
  savedCigars: number;
  savedCigarIds?: string[];
  savedQuestions?: number;
  onNavigate: (view: string) => void;
  onSelectFestival: (festivalId: string) => void;
  onSaveCigar?: (cigar: Cigar) => void;
  onLogSmoke?: (cigar: Cigar) => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  user,
  notes,
  savedCigars,
  savedCigarIds = [],
  onNavigate,
  onSelectFestival,
  onSaveCigar,
  onLogSmoke,
}) => {
  const recentNotes = notes.slice(0, 3);
  const averageScore = notes.length > 0
    ? Math.round(notes.filter(n => n.overallScore).reduce((sum, n) => sum + (n.overallScore || 0), 0) / notes.filter(n => n.overallScore).length)
    : 0;

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="flex-1 overflow-y-auto pb-20">
      {/* Welcome section */}
      <div className="px-4 py-6">
        <h1 className="text-2xl font-bold text-white mb-1">
          {getGreeting()}, {user.name.split(' ')[0]}
        </h1>
        <p className="text-white/60">Your daily cigar companion</p>
      </div>

      {/* Ash Wisdom - Compact quote at top */}
      <div className="px-4 mb-6">
        <AshWisdomCard compact />
      </div>

      {/* Today's Smoke - Hero feature */}
      <div className="px-4 mb-6">
        <TodaysSmokeCard
          onSave={onSaveCigar}
          onLogSmoke={onLogSmoke}
          isSaved={savedCigarIds.includes(getTodaysSmoke().id)}
        />
      </div>

      {/* Quick stats */}
      <div className="px-4 mb-6">
        <div className="grid grid-cols-3 gap-3">
          <StatCard
            label="Journal Entries"
            value={notes.length.toString()}
            icon={<BookOpen className="w-4 h-4" />}
          />
          <StatCard
            label="Saved Cigars"
            value={savedCigars.toString()}
            icon={<Star className="w-4 h-4" />}
          />
          <StatCard
            label="Avg Score"
            value={averageScore ? averageScore.toString() : '-'}
            icon={<TrendingUp className="w-4 h-4" />}
          />
        </div>
      </div>

      {/* Pairing of the Week */}
      <div className="px-4 mb-6">
        <PairingOfTheWeekCard />
      </div>

      {/* Main action cards */}
      <div className="px-4 space-y-4 mb-6">
        <ActionCard
          title="Cigar Explorer"
          description="Browse 50+ cigars, filter by strength, flavor, and more"
          icon={<Search className="w-6 h-6" />}
          color="amber"
          onClick={() => onNavigate('explorer')}
        />
        <ActionCard
          title="My Humidor"
          description={`${savedCigars} saved cigars • View recommendations`}
          icon={<Package className="w-6 h-6" />}
          color="stone"
          onClick={() => onNavigate('humidor')}
        />
        <ActionCard
          title="Cigar Advisor"
          description="Get personalized questions to ask vendors based on your taste profile"
          icon={<MessageSquare className="w-6 h-6" />}
          color="stone"
          onClick={() => onNavigate('advisor')}
        />
        <ActionCard
          title="My Journal"
          description={`${notes.length} tasting notes recorded`}
          icon={<BookOpen className="w-6 h-6" />}
          color="stone"
          onClick={() => onNavigate('journal')}
        />
        <ActionCard
          title="City Guides"
          description="Ybor City: lounges, local brands, history"
          icon={<Compass className="w-6 h-6" />}
          color="amber"
          onClick={() => onNavigate('city-guide')}
        />
        <ActionCard
          title="Festival Guide"
          description="Maps, vendor directory, and recommendations"
          icon={<Map className="w-6 h-6" />}
          color="stone"
          onClick={() => onNavigate('festivals')}
        />
        <ActionCard
          title="Band Gallery"
          description="Your cigar band collection"
          icon={<Image className="w-6 h-6" />}
          color="purple"
          onClick={() => onNavigate('band-gallery')}
        />
      </div>

      {/* Active festivals */}
      <div className="px-4 mb-6">
        <h2 className="text-lg font-semibold text-white mb-3">Upcoming Festivals</h2>
        <div className="space-y-2">
          {FESTIVALS.filter(f => f.active).slice(0, 3).map(festival => {
            const vendorCount = SAMPLE_VENDORS.filter(v => v.festivalId === festival.id).length;
            const daysUntil = getDaysUntil(festival.date);
            return (
              <button
                key={festival.id}
                onClick={() => onSelectFestival(festival.id)}
                className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <CigarIcon className="w-5 h-5 text-amber-500" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium">{festival.name}</h3>
                      {daysUntil && daysUntil <= 90 && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 text-xs font-medium">
                          {daysUntil}d
                        </span>
                      )}
                    </div>
                    <p className="text-white/50 text-sm">{festival.location}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {festival.date && (
                        <span className="flex items-center gap-1 text-amber-500/80 text-xs">
                          <Calendar className="w-3 h-3" />
                          {festival.date}
                        </span>
                      )}
                      {vendorCount > 0 && (
                        <span className="flex items-center gap-1 text-white/40 text-xs">
                          <Users className="w-3 h-3" />
                          {vendorCount} vendors
                        </span>
                      )}
                      {daysUntil && daysUntil > 90 && (
                        <span className="flex items-center gap-1 text-white/40 text-xs">
                          <Clock className="w-3 h-3" />
                          {daysUntil} days
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/40" />
              </button>
            );
          })}
          {FESTIVALS.filter(f => f.active).length > 3 && (
            <button
              onClick={() => onNavigate('festivals')}
              className="w-full py-3 text-amber-500 text-sm font-medium hover:text-amber-400"
            >
              View all {FESTIVALS.filter(f => f.active).length} festivals
            </button>
          )}
        </div>
      </div>

      {/* Recent journal entries */}
      {recentNotes.length > 0 && (
        <div className="px-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-white">Recent Notes</h2>
            <button
              onClick={() => onNavigate('journal')}
              className="text-amber-500 text-sm font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-2">
            {recentNotes.map(note => (
              <div
                key={note.id}
                className="p-3 rounded-xl bg-white/5 border border-white/10"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-white font-medium">{note.brand}</h3>
                    {note.line && <p className="text-white/50 text-sm">{note.line}</p>}
                  </div>
                  {note.overallScore && (
                    <div className="flex items-center gap-1 text-amber-500">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="font-medium">{note.overallScore}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full Ash Wisdom card at bottom */}
      <div className="px-4 mb-6">
        <AshWisdomCard />
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
}> = ({ label, value, icon }) => (
  <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
    <div className="flex justify-center text-amber-500 mb-1">{icon}</div>
    <div className="text-xl font-bold text-white">{value}</div>
    <div className="text-white/50 text-xs">{label}</div>
  </div>
);

const ActionCard: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  color: 'amber' | 'stone' | 'purple';
  onClick: () => void;
}> = ({ title, description, icon, color, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors text-left ${
      color === 'amber'
        ? 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20'
        : color === 'purple'
        ? 'bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20'
        : 'bg-white/5 border-white/10 hover:bg-white/10'
    }`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
      color === 'amber' ? 'bg-amber-500 text-stone-900' : color === 'purple' ? 'bg-purple-500 text-white' : 'bg-white/10 text-white'
    }`}>
      {icon}
    </div>
    <div className="flex-1">
      <h3 className="text-white font-semibold">{title}</h3>
      <p className="text-white/50 text-sm">{description}</p>
    </div>
    <ChevronRight className="w-5 h-5 text-white/40" />
  </button>
);

export default DashboardView;
